import React, { FC, memo } from 'react';
import { OrderHistoryItem } from '~/lib/data-contract/auto-generated/customer-model';
import { Typography } from '~/shared/components/typography/typography.component';
import styles from './order-list-item.module.css';
import { Button } from '~/shared/components/button/button.component';
import ChevronRightIcon from '$icons/chevron-right.svg';
import StoreIcon from '$icons/store.svg';
import GlobeIcon from '$icons/globe.svg';
import { Skeleton } from '~/shared/components/skeleton/skeleton.component';
import { useTranslation } from '~/shared/utils';
import ReturnIcon from '$icons/return.svg';

type Props = {
    order: OrderHistoryItem;
    onSelect?: (order: OrderHistoryItem) => void;
};

const OrderListItem: FC<Props> = memo((props) => {
    const { order, onSelect } = props;

    const { translate } = useTranslation();

    const handleSelect = () => {
        if (onSelect) {
            onSelect(order);
        }
    };

    if (!order) {
        return null;
    }

    const {
        purchaseSourceType,
        purchaseId,
        displayDate,
        purchaseSourceName,
        totalQuantity,
        displayTotalPrice,
        purchaseStatus,
    } = order;

    const isRefunded = purchaseStatus === 'Refunded';

    return (
        <li className={styles.root}>
            <div className={styles.iconCell}>
                {purchaseSourceType === 'Store' ? <StoreIcon /> : <GlobeIcon />}
            </div>
            <div className={styles.mainCell}>
                <Typography variant="caption" as="span">
                    {purchaseId}
                </Typography>
                <Typography
                    variant="bodyXs"
                    as="span"
                    className={styles.text}
                    fontWeight="semibold"
                >
                    {displayDate}
                </Typography>
                <Typography variant="bodyXs" as="span" className={styles.text}>
                    {purchaseSourceName}
                </Typography>
            </div>
            <div className={styles.priceQuantityCell}>
                <Typography variant="bodyXs" nowrap as="span" className={styles.text}>
                    {totalQuantity}{' '}
                    {translate(`club.orderHistory.${totalQuantity === 1 ? 'item' : 'items'}`)}
                </Typography>
                {isRefunded && <ReturnIcon className={styles.refundIcon} />}
                <Typography
                    variant="bodyXs"
                    nowrap
                    as="span"
                    className={styles.text}
                >
                    {displayTotalPrice}
                </Typography>
            </div>
            <div className={styles.chevronCell}>
                <ChevronRightIcon />
            </div>
            <div className={styles.actionCell}>
                <Button
                    className={styles.button}
                    variant="secondary"
                    hoverEffect
                    type="button"
                    onClick={handleSelect}
                >
                    {translate('club.orderHistory.viewDetailsButton')}
                </Button>
            </div>
        </li>
    );
});

export const OrderListItemSkeleton: FC = () => (
    <li className={styles.root}>
        <div className={styles.iconCell} />
        <div className={styles.mainCell} style={{ gap: '5px' }}>
            <Skeleton width="40%" height="12px" />
            <Skeleton width="60%" height="16px" />
            <Skeleton width="70%" height="16px" />
        </div>
        <div className={styles.priceQuantityCell}>
            <Skeleton width="60px" height="18px" />
            <Skeleton width="100px" height="18px" />
        </div>
        <div className={styles.chevronCell}>
            <ChevronRightIcon />
        </div>
        <div className={styles.actionCell}>
            <Skeleton width="150px" height="40px" />
        </div>
    </li>
);

OrderListItem.displayName = 'OrderListItem';
export default OrderListItem;
